import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import clsx from "clsx";

// Interfaces
import { IGeneralParamsProps, IOption, ISmartBasicsProps, ISmartSelectorProps, TSelectValue } from "./interfaces";

// Helpers
import getRenderTags from "./RenderTags"; // Special RenderTags function
import {
	getAppendingOptions,
	getFilteredOptions,
	getMappedSelection,
	getOptionLabel,
	getPaperProps,
	getPopperProps,
	getSelectedOption,
	getSortedOptions,
	isAddingOption,
	isAllChecked,
	isAllDisabled,
	isAllSelectable,
	isAllSelected,
	isCompatibleOption,
	isHiddenInput,
} from "./utils";

// MUI
import { Autocomplete, CircularProgress, FormControl } from "@mui/material";

// Components
import RenderGroup from "./RenderGroup";
import RenderInputField from "./RenderInputField";
import RenderOption from "./RenderOption";

// Icons
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ClearIcon from "@mui/icons-material/Clear";

// Constants
const MANDATORY_CLOSE = ["blur", "toggleInput", "escape"];

const isDisabled = (option: IOption, disabledOptions: string[] = [], selectBy = "_id"): boolean => {
	const { [selectBy as keyof object]: id } = option || {};
	return disabledOptions.includes(id) || Boolean(option?.disabled);
};

const getIconsClasses = (disabled = false) => {
	return clsx({
		"!text-default": !disabled,
		"!text-disabled": disabled,
	});
};

const RenderPopupIndicator = (props: Readonly<ISmartBasicsProps & IGeneralParamsProps>) => {
	const { icon = null, loading = false, disabled = false } = props;

	if (loading) {
		return <CircularProgress color="inherit" size={20} />;
	}

	if (icon) {
		return icon;
	}

	return <ArrowForwardIosRoundedIcon className={getIconsClasses(disabled)} sx={{ transform: "rotate(90deg)", fontSize: "18px" }} />;
};

const getSelectAllOption = (label = "Select All", callback = () => {}, args: ISmartBasicsProps | null = null) => {
	const { selectBy = "_id", showBy = "label", checked = false } = args ?? {};
	return {
		checked,
		isSpecialOption: true,
		[selectBy]: "selectAll",
		[showBy]: label,
		onClickOption: callback,
	};
};

const getSelectedOptionLabel = (props: Readonly<{ option: object; optionLabelProps?: object; args: ISmartBasicsProps }>) => {
	const { option, optionLabelProps = undefined, args = undefined } = props;
	const { selectBy = "_id", showBy = "label", sortBy = "" } = args || {};
	const { formatter = undefined } = (optionLabelProps ?? {}) as { formatter?: Function };
	if (formatter) {
		return formatter?.(option) as string;
	}
	return getOptionLabel(option, { selectBy, showBy, sortBy });
};

const getValue = (value: TSelectValue) => {
	if (!Array.isArray(value)) {
		return value;
	}
	return value.filter((v) => !v.isSpecialOption);
};

export default function SmartSelector(props: Readonly<ISmartSelectorProps>) {
	const {
		options = [],
		disabledOptions = [],
		actionsProps = undefined,
		groupingProps = undefined,
		inputProps = undefined,
		listBoxProps = undefined,
		optionLabelProps = undefined,
		paperProps = undefined,
		popperProps = undefined,
		popupIconProps = undefined,
		renderOptionCustomProps = undefined,
		selected = null,
		selectBy = "_id",
		showBy = "label",
		sortBy = "",
		sortMethod = "ASC",
		classes = "",
		tagsVariant = undefined,
		limitTags = undefined,
		addable = false,
		appendOptions = false,
		clearable = false,
		disabled = false,
		disablePortal = false,
		warning = false,
		error = false,
		hideTooltip = false,
		highlighted = false,
		loading = false,
		multiple = false,
		multipleSelectAll = false,
		selectDefault = false,
		showAlways = false,
		sx = null,
		onBlurAction = () => {},
		onChangeAction = () => {},
		onCloseAction = () => {},
		onFocusAction = () => {},
		onOpenAction = () => {},
		onInputChangeAction = () => {},
	} = props;

	const { renderTagsCustomProps = {} } = props;
	const { showTagBy = showBy } = renderTagsCustomProps;
	const { groupBy = "" } = groupingProps || {};

	const [open, setOpen] = useState(false);
	const [isBlurred, setIsBlurred] = useState(true);
	const [value, setValue] = useState<TSelectValue>(multiple ? [] : null);

	// Local constants
	const allDisabled = isAllDisabled(disabled, loading);
	const allSelectable = isAllSelectable(multiple, multipleSelectAll);
	const allChecked = isAllChecked(options, value as object[]);
	const allSelected = isAllSelected(allSelectable, allChecked);
	const hiddenInput = isHiddenInput({ isBlurred, multiple, tagsVariant, value: value as object[] });

	// Handlers
	const handleChange = (_event: SyntheticEvent, selection: TSelectValue) => {
		const isAdding = isAddingOption(selection, addable);

		if (isAdding) {
			const mapped = getMappedSelection(selection, { selectBy, showBy, multiple });
			setValue(mapped);
			onChangeAction?.(mapped);
			onCloseAction?.();
			setOpen(false);
			setIsBlurred(true);
			return;
		}

		setValue(selection);
		(!multiple || !open) && onChangeAction?.(selection);
	};

	const handleClose = (event: SyntheticEvent, reason: string) => {
		const { type } = event || {};
		const closeOnSelection = reason === "selectOption" && !multiple;
		const close = (MANDATORY_CLOSE.includes(reason) || closeOnSelection) && type !== "mousedown";

		if (close) {
			multiple && onChangeAction?.(value);
			setOpen(false);
			onCloseAction?.();
		}
	};

	const handleOpen = (event?: SyntheticEvent) => {
		setOpen(true);
		onOpenAction?.(event);
	};

	const handleSelectAll = () => {
		let values: object[] = [];
		if (!allSelected) {
			values = [...options];
			onCloseAction?.();
		}
		setValue(values);
		setIsBlurred(true);
		onChangeAction?.(values);
	};

	const dataOptions = useMemo(() => getAppendingOptions(options, { value, appendOptions, multiple, selectBy }), [options, appendOptions]);
	const sortedOptions = getSortedOptions(dataOptions, sortBy, sortMethod);
	const optionsToShow = allSelectable
		? [getSelectAllOption("Select All", handleSelectAll, { selectBy, showBy, checked: allSelected }), ...sortedOptions]
		: sortedOptions;

	useEffect(() => {
		if (selected) {
			let defaultSelection: TSelectValue | string = selected;

			if (!selected && selectDefault) {
				defaultSelection = options?.[0] ?? null;
			}

			if (typeof selected === "string" && !selectDefault) {
				const option = getSelectedOption(options, selected, selectBy) ?? null;
				defaultSelection = option as TSelectValue;
				if (multiple) {
					defaultSelection = option ? [option] : [];
				}
			}
			setValue(defaultSelection as TSelectValue);
		}

		return () => {
			setValue(multiple ? [] : null);
		};
	}, [selected, selectDefault]);

	return (
		<FormControl fullWidth sx={{ minWidth: 30 }}>
			<Autocomplete
				fullWidth
				size="small"
				className={classes}
				open={open || showAlways}
				value={getValue(value)}
				options={optionsToShow}
				multiple={multiple}
				limitTags={limitTags ?? (multiple ? 2 : -1)}
				popupIcon={<RenderPopupIndicator {...popupIconProps} loading={loading} disabled={allDisabled} />}
				clearIcon={clearable && <ClearIcon className={getIconsClasses(allDisabled)} sx={{ fontSize: "18px" }} />}
				groupBy={(option) => option?.[groupBy as keyof object] ?? ""}
				getOptionLabel={(option: object) =>
					getSelectedOptionLabel({ option, optionLabelProps, args: { selectBy, showBy, sortBy } })
				}
				getOptionDisabled={(option: IOption) => isDisabled(option, disabledOptions, selectBy) || allDisabled}
				filterOptions={(innerOptions, state) => getFilteredOptions(innerOptions, state, { selectBy, showBy, addable })}
				renderInput={(params) => (
					<RenderInputField
						{...inputProps}
						params={params}
						selectorProps={props}
						value={value}
						disabled={allDisabled}
						warning={warning}
						error={!loading && error}
						hidden={hiddenInput}
						highlighted={highlighted}
						multiple={multiple}
						open={open}
						onInputChange={onInputChangeAction}
					/>
				)}
				renderGroup={(params) => {
					const { key, ...groupProps } = params;
					return <RenderGroup key={key} index={key} {...groupProps} groupingProps={groupingProps} />;
				}}
				renderOption={(renderProps, option: IOption, args) => {
					const { key, ...optionProps } = renderProps;
					const { [selectBy as keyof object]: identifier, [showBy as keyof object]: title } = option || {};
					const optionKey = `${key}-${identifier}-${title}`;

					return (
						<RenderOption
							key={optionKey}
							renderProps={optionProps}
							actionsProps={actionsProps}
							renderOptionCustomProps={renderOptionCustomProps}
							option={option}
							args={{ ...args, showBy, selectBy, multiple, disabled: allDisabled }}
						/>
					);
				}}
				renderTags={(tags, getTagProps) =>
					getRenderTags({
						showBy: showTagBy,
						selectBy,
						tags,
						tagsVariant,
						getTagProps,
						hideTooltip,
						renderTagsCustomProps,
						disabled: allDisabled,
						hidden: hiddenInput,
					})
				}
				componentsProps={{
					popper: getPopperProps(popperProps),
					paper: getPaperProps(paperProps),
				}}
				ListboxProps={{
					className: "[&>li:last-child>hr]:hidden",
					...listBoxProps,
				}}
				isOptionEqualToValue={(option, current) => isCompatibleOption(option, current, selectBy)}
				disabled={allDisabled}
				disableClearable={!clearable || allDisabled}
				disablePortal={disablePortal}
				sx={sx}
				onChange={handleChange}
				onOpen={handleOpen}
				onClose={handleClose}
				onBlur={() => {
					setIsBlurred(true);
					onBlurAction?.();
				}}
				onFocus={() => {
					setIsBlurred(false);
					if (!open) {
						setOpen(true);
					}
					onFocusAction?.();
				}}
			/>
		</FormControl>
	);
}
